import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useState } from 'react';
import { Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { Link, Route, Routes } from 'react-router-dom';

import { getDrivers } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import { Driver } from '../../types';
import AnalyticsHeader from '../../widgets/Header';
import CreateDriverModal from './CreateDriverModal';
import DriverDetail from './ViewAndEditDetail/ViewAndEditDetail';

export const GET_DRIVERS_KEY = ['drivers'];

const CreateDriverButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}> Create Driver </Button>;
};

const columnHelper = createColumnHelper<Driver>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('phoneNumber', {
    header: 'Phone',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('nickName', {
    header: 'Nickname',
    cell: info => info.getValue(),
    footer: info => info.column.id
  })
];

interface DriverTableProps {
  query: Record<string, unknown>;
}

const DriversTable = ({ query }: DriverTableProps) => {
  const [showCreateDriverModal, setShowCreateDriverModal] = useState(false);
  const compositeQueryKey = [...GET_DRIVERS_KEY, query];
  const { data, isLoading } = useQuery({ queryKey: compositeQueryKey, queryFn: () => getDrivers(query) });
  const drivers = data?.results;

  const onCreateButtonClicked = () => {
    setShowCreateDriverModal(true);
  };

  const onCreateModalClosed = () => {
    setShowCreateDriverModal(false);
  };

  return (
    <>
      {showCreateDriverModal && <CreateDriverModal show={showCreateDriverModal} handleClose={onCreateModalClosed} />}
      <AnalyticsHeader
        title="Drivers"
        headerButton={<CreateDriverButton onClick={onCreateButtonClicked} />}
        tabs={tabRoutes}
      />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : drivers && drivers.length > 0 ? (
              <TableList drivers={drivers!} columns={columns} />
            ) : (
              <EmptyTableState message="No drivers yet." />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const tabRoutes = [
  {
    title: 'Active',
    path: 'active',
    tabLink: '/drivers/active',
    element: <DriversTable query={{ isActive: true }} />
  },
  {
    title: 'Inactive',
    path: 'inactive',
    tabLink: '/drivers/inactive',
    element: <DriversTable query={{ isActive: false }} />
  }
];

const driverRoutes = [
  {
    path: '/:status/:id',
    element: <DriverDetail />
  }
];

const Drivers = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
          {driverRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default Drivers;

interface TableListProps {
  drivers: Driver[];
  columns: AccessorKeyColumnDef<Driver, string>[];
}

const TableList = ({ drivers, columns }: TableListProps) => {
  const tableInstance = useReactTable({ data: drivers!, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <Card>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <td key={cell.id}>
                      <Link to={`../driver/${row.original._id}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
